import { AccountingAccountResponse } from '@/app/models/accountingAcount';
import {
  useCreateAccountingAccountMutation,
  useUpdateAccountingAccountMutation,
} from '@/app/services/accountingAccountApi';
import PageTitle from '@/components/pageTitle';
import {
  Box,
  Button,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

interface NewEditEventFileScreenProps {
  accountingAccount: AccountingAccountResponse | null;
  setDrawerOpen: (open: boolean) => void;
}

export default function NewEditAccountingAccountScreen(props: NewEditEventFileScreenProps) {
  const { t } = useTranslation(['accountingAccount', 'common']);

  const { accountingAccount, setDrawerOpen } = props;

  const [accountType, setAccountType] = useState(accountingAccount?.accountType || '');
  const [subType, setSubType] = useState(accountingAccount?.subType || '');
  const [accountCode, setAccountCode] = useState(accountingAccount?.accountCode || '');

  const [createAccountingAccount] = useCreateAccountingAccountMutation();
  const [updateAccountingAccount] = useUpdateAccountingAccountMutation();

  const handleSubmit = async () => {
    if (accountingAccount && accountType && subType && accountCode) {
      const response = await updateAccountingAccount({
        id: accountingAccount.id,
        accountType,
        subType,
        accountCode,
      });
      if ('data' in response) {
        toast.success(
          t('updated.accouting.account', {
            position: toast.POSITION.TOP_RIGHT,
          }),
        );
      }
    } else if (accountType && subType && accountCode) {
      const response = await createAccountingAccount({
        accountType,
        subType,
        accountCode,
      });
      if ('data' in response) {
        toast.success(
          t('added.accouting.account', {
            position: toast.POSITION.TOP_RIGHT,
          }),
        );
      }
    }
  };

  const disableCreate = !(accountCode && accountType && subType);
  const disableUpdate =
    disableCreate ||
    (accountType === accountingAccount?.accountType &&
      subType === accountingAccount?.subType &&
      accountCode === accountingAccount?.accountCode);

  return (
    <div
      style={{
        width: 480,
        backgroundColor: '#FAFAFA',
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        padding: '24px',
      }}
    >
      <PageTitle title={t('common:accounting.accounts')} />
      <div style={{ marginBottom: '24px' }} />

      <Box
        sx={{
          display: 'flex',
          width: '100%',
          gap: 2,
          marginTop: 3,
        }}
      >
        <Box sx={{ flexGrow: 1, width: '50%' }}>
          <Typography variant="h3">
            <span style={{ color: 'orange' }}> * </span>
            {t('operation.type')}
          </Typography>
          <Select
            value={accountType}
            size="small"
            placeholder={t('payroll') as string}
            sx={{ width: '100%', fontSize: '13px', marginTop: '4px' }}
            onChange={(event: SelectChangeEvent<string>) => {
              setAccountType(event.target.value as string);
            }}
          >
            <MenuItem value="Folha">{t('payroll')}</MenuItem>
            <MenuItem value="Encargos">{t('charges')}</MenuItem>
            <MenuItem value="Encargos Incentivados">{t('incentivized.charges')}</MenuItem>
            <MenuItem value="Folha Debit">{t('payroll.debit')}</MenuItem>
          </Select>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          gap: 2,
          marginTop: 3,
        }}
      >
        <Box sx={{ flexGrow: 1, width: '50%' }}>
          <Typography variant="h3">
            <span style={{ color: 'orange' }}> * </span>
            {t('common:type.project')}
          </Typography>
          <Select
            value={subType}
            size="small"
            placeholder={t('Selecione a Classificação de CR') as string}
            sx={{ width: '100%', fontSize: '13px', marginTop: '4px' }}
            onChange={(event: SelectChangeEvent<string>) => {
              setSubType(event.target.value as string);
            }}
          >
            <MenuItem value="ESPECIAL">{t('special')}</MenuItem>
            <MenuItem value="INCENTIVADO">{t('incentivized')}</MenuItem>
            <MenuItem value="PRIVADO">{t('private')}</MenuItem>
            <MenuItem value="N/A">{t('na')}</MenuItem>
          </Select>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          gap: 2,
          marginTop: 3,
        }}
      >
        <Box sx={{ flexGrow: 1, width: '50%' }}>
          <Typography variant="h3">
            <span style={{ color: 'orange' }}> * </span>
            {t('common:accounting.accounts')}
          </Typography>
          <TextField
            value={accountCode}
            onChange={(e) => setAccountCode(e.target.value)}
            variant="outlined"
            size="small"
            placeholder={t('Digite a Classificação de CR') as string}
            sx={{ width: '100%', fontSize: '13px', marginTop: '4px' }}
          />
        </Box>
      </Box>

      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          marginBottom: '8px',
          marginTop: 'auto',
        }}
      >
        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="baseline"
          margin={3}
          spacing={2}
        >
          <Button
            variant="secondary"
            onClick={() => {
              setDrawerOpen(false);
            }}
          >
            {t('common:cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              handleSubmit();
              setDrawerOpen(false);
            }}
            disabled={accountingAccount ? disableUpdate : disableCreate}
          >
            {t('common:save')}
          </Button>
        </Stack>
      </div>
    </div>
  );
}
